import { cn } from "@design-system/utils";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { Loader } from "../Loader";

export const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input dark:border-gray-600 bg-background dark:bg-gray-800 shadow-sm hover:bg-accent dark:hover:bg-gray-700 hover:text-accent-foreground dark:hover:text-gray-100",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        icon: "h-8 w-8 rounded-md",
        xs: "h-7 px-2 rounded-md text-xs",
        sm: "h-8 px-3 rounded-md text-sm",
        md: "h-9 px-4 rounded-md text-sm",
        lg: "h-10 px-6 rounded-md text-base",
        xl: "h-12 px-8 rounded-md text-lg",
      },
      color: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        red: "bg-red-500 text-white hover:bg-red-600",
        green: "bg-green-500 text-white hover:bg-green-600",
        blue: "bg-blue-500 text-white hover:bg-blue-600",
        orange: "bg-orange-500 text-white hover:bg-orange-600",
        yellow: "bg-yellow-500 text-black hover:bg-yellow-600",
        lime: "bg-lime-500 text-black hover:bg-lime-600",
        teal: "bg-teal-500 text-white hover:bg-teal-600",
        dark: "bg-gray-800 text-white hover:bg-gray-900",
        white: "bg-white text-black hover:bg-gray-100",
        purple: "bg-purple-500 text-white hover:bg-purple-600",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  color?: NonNullable<VariantProps<typeof buttonVariants>["color"]>;
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      className,
      variant,
      size,
      color,
      isLoading,
      children,
      ...props
    },
    ref,
  ) => {
    const content = isLoading ? <Loader /> : children;

    return (
      <button
        type={type}
        className={cn(buttonVariants({ variant, size, color, className }))}
        ref={ref}
        {...props}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = "Button";
